import {
  alarmConditionItems,
  alarmCondMethodItems,
  getOptions
} from '@/shared/SelectData'
import {
  AlarmItem,
  TemplateAlarmEditProps,
  VariableItem
} from '@/template/interface'
import { TemplateReference } from '@/template/reference'
import { getRefTypeDesc } from '@/template/reference/util'
import { getAddressOption } from '@/utils/dataType'
import { Guid } from '@/utils/guid'
import { getFormatMessage } from '@/utils/utils'
import { Col, Form, Input, InputNumber, Radio, Row, Select } from 'antd'
import React, { Fragment, useEffect, useState } from 'react'

export const TemplateAlarmEdit: React.FC<TemplateAlarmEditProps> = (
  props: TemplateAlarmEditProps
) => {
  const [form] = Form.useForm<AlarmItem>()
  const [condMethod, setCondMethod] = useState<number>(0)
  const [selectVariable, setSelectVariable] = useState<VariableItem>()
  const [referenceVisible, setReferenceVisible] = useState<boolean>(false)
  const formatMessage = getFormatMessage()
  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 4 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 18 }
    }
  }
  const formItemStyle = { marginBottom: 0 }
  const excludeTypes = [0, 30]
  const disabled = props.references && props.references.length > 0

  const handleVariableChange = (id: string) => {
    const variable = props.variables?.find((x) => x.id == id)
    setSelectVariable(variable)
    if (props.item && !props.item.id) {
      const operate1 =
        variable && (variable.dataType == 0 || variable.enabledIndex) ? 1 : 0
      form.setFieldsValue({ operate1: operate1 })
    }
  }

  const handleCondMethodChange = (value: number) => {
    setCondMethod(value)
  }

  const getDeadType = (dtType) => {
    // 位类型不支持死区
    if (excludeTypes.includes(dtType)) {
      return 0
    }

    // 浮点数死区类型4
    if ([16].includes(dtType)) {
      return 4
    }

    // 整数 死区类型2
    return 2
  }

  useEffect(() => {
    if (props.item) {
      form.setFieldsValue(props.item)
      setCondMethod(props.item.condMethod || 0)
      handleVariableChange(props.item?.variableId || '')
    }
  }, [props.item])

  useEffect(() => {
    if (props.formResult) {
      form
        .validateFields()
        .then((values: AlarmItem) => {
          const merged = Object.assign(props.item, values)
          merged.dead_type = getDeadType(selectVariable?.dataType)
          if (props.onFormValidate) {
            if (!props.onFormValidate(true, merged)) return
          }
          if (props.onFormSubmit) {
            if (!merged.id) {
              merged.id = Guid.raw()
            }
            props.onFormSubmit(merged)
            form.resetFields()
          }
        })
        .catch((info) => {
          console.log('Validate Failed:', info)
          if (props.onFormValidate) {
            props.onFormValidate(false)
          }
        })
    }
  }, [props.formResult])

  return (
    <Fragment>
      <Form form={form} {...formItemLayout}>
        <Form.Item
          label={formatMessage({ id: 'app.template.alarm.edit.variableId' })}
          name='variableId'
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'app.template.alarm.edit.variableId.message'
              })
            }
          ]}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            onChange={handleVariableChange}
            disabled={disabled}
          >
            {props.variables?.map((x) => (
              <Select.Option value={x.id!} key={x.id}>
                {getAddressOption(x, props.plcs)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={formatMessage({ id: 'app.template.alarm.edit.name' })}
          name='name'
          rules={[
            {
              required: true,
              transform: (value) => value?.trim(),
              message: formatMessage({
                id: 'app.template.alarm.edit.name.message'
              })
            }
          ]}
        >
          <Input maxLength={15} />
        </Form.Item>

        {(selectVariable?.dataType == 0 || selectVariable?.enabledIndex) && (
          <Form.Item
            label='位设置'
            name='operate1'
            rules={[
              {
                required: true,
                message: '报警条件不能为空'
              }
            ]}
          >
            <Radio.Group>
              <Radio value={1}>ON</Radio>
              <Radio value={0}>OFF</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        {selectVariable?.dataType != 0 && !selectVariable?.enabledIndex && (
          <Form.Item label='条件(二进制)'>
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item style={formItemStyle} name='condition1'>
                  <Select>{getOptions(alarmConditionItems)}</Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  style={formItemStyle}
                  name='operate1'
                  rules={[
                    {
                      required: true,
                      message: '报警条件不能为空'
                    }
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item style={formItemStyle} name='condMethod'>
                  <Select onChange={handleCondMethodChange}>
                    {getOptions(alarmCondMethodItems)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {condMethod != 0 && (
              <Row gutter={24} style={{ marginTop: '20px' }}>
                <Col span={8}>
                  <Form.Item style={formItemStyle} name='condition2'>
                    <Select>{getOptions(alarmConditionItems)}</Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    style={formItemStyle}
                    name='operate2'
                    rules={[
                      {
                        required: true,
                        message: '报警条件不能为空'
                      }
                    ]}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Form.Item>
        )}

        {!excludeTypes.includes(selectVariable?.dataType || 0) &&
          !selectVariable?.enabledIndex && (
            <Form.Item
              label={formatMessage({ id: 'app.template.alarm.edit.deadValue' })}
              name='dead_value'
            >
              <InputNumber style={{ width: '30%' }} step={0.1} />
            </Form.Item>
          )}

        <Form.Item
          label={formatMessage({ id: 'app.template.alarm.edit.msg' })}
          name='msg'
          rules={[
            {
              required: true,
              transform: (value) => value?.trim(),
              message: formatMessage({
                id: 'app.template.alarm.edit.msg.message'
              })
            }
          ]}
        >
          <Input.TextArea style={{ minHeight: 50 }} maxLength={60} />
        </Form.Item>

        {disabled && (
          <Form.Item>
            <span style={{ color: 'red' }}>
              {formatMessage({
                id: 'app.template.alarm.edit.references',
                parameters: { refs: getRefTypeDesc(props.references!) }
              })}
            </span>
            <a
              onClick={() => {
                setReferenceVisible(true)
              }}
            >
              {formatMessage({ id: 'app.template.reference.view' })}
            </a>
          </Form.Item>
        )}
      </Form>
      <TemplateReference
        data={props.references}
        visible={referenceVisible}
        onVisibleChange={setReferenceVisible}
        onRefRelease={props.onRefRelease}
      />
    </Fragment>
  )
}
